import React, { useMemo, useCallback, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { Box, DropzoneFileListItem } from '@cad-arc';
import { AttachmentDto } from '@mark43/cad-api';
import {
    convertAttachmentsToCombinedItems,
    getDownloadUrl,
    getFileTypeFromExtension,
    sortAttachments,
} from './helpers';
import { AttachmentUpload } from './attachments-upload-store';
import { AttachmentSortOptionValue, AttachmentsPanelType } from './types';
import { DEFAULT_ATTACHMENT_SORT_OPTION_VALUE } from './constants';
import { AttachmentThumbnailItem } from './attachment-thumbnail-item';
import { useDeleteAttachment } from './hooks/use-delete-attachment';
import { showDeleteAttachmentConfirmationModal } from './show-delete-attachment-confirmation-modal';
import { Tooltip } from '../common/components/tooltip';
import { useAttachmentsAbilities } from './hooks/use-attachments-abilities';
import { useAttachmentSizeLimits } from './hooks/use-attachment-size-limits';

export interface AttachmentsThumbnailGridProps {
    attachments: AttachmentDto[];
    entityId: number;
    entityType: AttachmentsPanelType;
    onOpenPreview?: (attachmentId: number) => void;
    variant?: 'grid' | 'thumbnails';
    uploads?: AttachmentUpload[];
    getUploadProgress?: (id: string) => number;
    sortOption?: AttachmentSortOptionValue;
    onClickRemoveFailedUpload: (upload: AttachmentUpload) => void;
    isMergedAttachment?: boolean;
    isClearedEvent?: boolean;
}

export const AttachmentsThumbnailGrid = observer(function AttachmentsThumbnailGrid({
    attachments,
    entityId,
    entityType,
    onOpenPreview,
    variant = 'grid',
    uploads = [],
    getUploadProgress,
    onClickRemoveFailedUpload,
    sortOption = DEFAULT_ATTACHMENT_SORT_OPTION_VALUE,
    isMergedAttachment,
    isClearedEvent,
}: AttachmentsThumbnailGridProps): React.ReactElement {
    const {
        imageMaxPreviewSizeBytes,
        pdfMaxPreviewSizeBytes,
        videoMaxPreviewSizeBytes,
        thumbnailMaxSizeBytes,
    } = useAttachmentSizeLimits();
    // Delete mutation has to be hoisted to parent of AttachmentThumbnailItem to prevent memory leaks when item unmounts (attachment is deleted)
    const deleteAttachmentMutation = useDeleteAttachment({ entityId, entityType });
    const { canDeleteAttachments } = useAttachmentsAbilities(entityType);
    const cancelButtonRef = useRef<HTMLButtonElement | null>(null);

    const handleDeleteAttachment = useCallback(
        function handleDelete(
            attachmentId: number,
            attachmentType: AttachmentDto['attachmentType']
        ) {
            const attachment = attachments.find((att) => att.attachmentId === attachmentId);
            if (!canDeleteAttachments || !attachment) return;

            showDeleteAttachmentConfirmationModal({
                attachment,
                onConfirm: () => {
                    deleteAttachmentMutation.mutate({
                        attachmentId,
                        attachmentType,
                        entityId,
                    });
                },
                cancelButtonRef,
                isLoading: deleteAttachmentMutation.isLoading,
            });
        },
        [attachments, deleteAttachmentMutation, entityId, canDeleteAttachments]
    );

    // Sort attachments based on the selected sort option
    const sortedAttachments = useMemo(
        () => sortAttachments(attachments, sortOption),
        [attachments, sortOption]
    );

    const gridItems = useMemo(
        () =>
            convertAttachmentsToCombinedItems({
                attachments: sortedAttachments,
                previewSizeLimits: {
                    imageMaxPreviewSizeBytes,
                    pdfMaxPreviewSizeBytes,
                    videoMaxPreviewSizeBytes,
                    thumbnailMaxSizeBytes,
                },
            }),
        [
            sortedAttachments,
            imageMaxPreviewSizeBytes,
            pdfMaxPreviewSizeBytes,
            videoMaxPreviewSizeBytes,
            thumbnailMaxSizeBytes,
        ]
    );

    // Filter active uploads to show only for grid variant
    const uploadsToShow = variant === 'grid' ? uploads : [];

    if (attachments.length === 0 && uploadsToShow.length === 0) {
        return <Box />;
    }

    return (
        <Box
            display="grid"
            gridTemplateColumns={
                variant === 'grid'
                    ? 'repeat(auto-fill, minmax(96px, 96px))'
                    : 'repeat(auto-fill, minmax(80px, 1fr))'
            }
            gap={4}
            width="100%"
            sx={{
                // Performance optimization: skip rendering work for off-screen items
                '& > *': {
                    contentVisibility: 'auto',
                },
            }}
        >
            {uploadsToShow.map((upload) => {
                const fileType = getFileTypeFromExtension(upload.fileName);
                const progress = getUploadProgress ? getUploadProgress(upload.id).toFixed(1) : 0;
                const status = upload?.status === 'failed' ? 'error' : 'loading';
                const displayName =
                    status === 'error' ? upload.fileName : `${progress}% - ${upload.fileName}`;

                const fileListItem = (
                    <DropzoneFileListItem
                        key={upload.id}
                        file={{
                            size: upload.fileSize,
                            name: displayName,
                        }}
                        // @ts-expect-error fileType is not exposed as a prop - remove after arc types updated
                        fileType={fileType}
                        status={status}
                        size={variant === 'grid' ? 'md' : 'sm'}
                        actions={
                            status === 'error'
                                ? [
                                      {
                                          label: 'Delete',
                                          visual: 'TrashCan',
                                          action: () => onClickRemoveFailedUpload(upload),
                                      },
                                  ]
                                : undefined
                        }
                    />
                );

                // Wrap with tooltip only if there's an error
                return status === 'error' ? (
                    <Tooltip
                        key={upload.id}
                        overlay="Upload failed. Please try again."
                        placement="top"
                    >
                        {fileListItem}
                    </Tooltip>
                ) : (
                    fileListItem
                );
            })}
            {gridItems.map((item) => {
                const attachment = sortedAttachments.find(
                    (att) => att.attachmentId.toString() === item.id
                );
                if (!attachment) return null;

                // Use the attachment-derived download URL (not item.src) since files that are too large to preview
                // will not have a src populated that can be used to download the file.
                const downloadUrl = getDownloadUrl(attachment);
                return (
                    <AttachmentThumbnailItem
                        key={item.id}
                        attachmentId={attachment.attachmentId}
                        attachmentType={attachment.attachmentType}
                        showActionsMenu={variant === 'grid'}
                        thumbnailSrc={item.thumbnailSrc ?? ''}
                        fileType={item.type}
                        fileName={item.title ?? ''}
                        downloadUrl={downloadUrl}
                        onOpenPreview={onOpenPreview}
                        onDelete={handleDeleteAttachment}
                        hideDelete={!canDeleteAttachments || isClearedEvent || isMergedAttachment}
                    />
                );
            })}
        </Box>
    );
});
